<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const botEnabled = ref(false);
const selectedPersonaId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);

const personas = computed(() => store.getters['botPersonas/getPersonas']);

onMounted(async () => {
  isLoading.value = true;
  await store.dispatch('botPersonas/fetch');
  const setting = store.getters['conversationBot/getInboxBotSetting'](
    props.inbox.id
  );
  if (setting) {
    botEnabled.value = setting.bot_enabled;
    selectedPersonaId.value = setting.bot_persona_id;
  } else {
    await store.dispatch('conversationBot/fetchInboxSetting', props.inbox.id);
    const freshSetting = store.getters['conversationBot/getInboxBotSetting'](
      props.inbox.id
    );
    if (freshSetting) {
      botEnabled.value = freshSetting.bot_enabled;
      selectedPersonaId.value = freshSetting.bot_persona_id;
    }
  }
  isLoading.value = false;
});

async function save() {
  isSaving.value = true;
  await store.dispatch('conversationBot/updateInboxSetting', {
    inboxId: props.inbox.id,
    bot_enabled: botEnabled.value,
    bot_persona_id: selectedPersonaId.value || null,
  });
  useAlert(t('MINISTRY.INBOX_BOT.SAVE_SUCCESS'));
  isSaving.value = false;
}
</script>

<template>
  <div class="mx-8 mt-6 max-w-2xl">
    <div v-if="isLoading" class="flex justify-center py-12">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-8" />
    </div>

    <div v-else class="flex flex-col gap-6">
      <div
        class="rounded-xl border border-n-weak bg-white p-5 flex flex-col gap-4"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('MINISTRY.INBOX_BOT.TITLE') }}
        </h3>

        <div class="flex items-center justify-between">
          <label class="text-sm text-n-slate-11">
            {{ t('MINISTRY.INBOX_BOT.ENABLE_LABEL') }}
          </label>
          <input
            v-model="botEnabled"
            type="checkbox"
            class="size-4 rounded accent-woot-500"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('MINISTRY.INBOX_BOT.PERSONA_LABEL') }}
          </label>
          <select
            v-model="selectedPersonaId"
            :disabled="!botEnabled"
            class="w-full rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-400 disabled:opacity-50"
          >
            <option :value="null">
              {{ t('MINISTRY.INBOX_BOT.PERSONA_PLACEHOLDER') }}
            </option>
            <option
              v-for="persona in personas"
              :key="persona.id"
              :value="persona.id"
            >
              {{ persona.name }}
            </option>
          </select>
          <p v-if="personas.length === 0" class="text-xs text-n-slate-7">
            {{ t('MINISTRY.INBOX_BOT.NO_PERSONAS') }}
          </p>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          :disabled="isSaving"
          class="px-4 py-2 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors disabled:opacity-50"
          @click="save"
        >
          <span
            v-if="isSaving"
            class="i-lucide-loader-2 size-3.5 animate-spin mr-1"
          />
          {{ t('MINISTRY.INBOX_BOT.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
